import type { ReviewResidentPayload } from '../types/domain'
import { DOCUMENT_TYPE_LABELS, type ReimbursementDocumentType } from '../types/reimbursementFormTypes'

/** Backend `REIMBURSEMENT_DOCUMENT_TYPES` request keys for `POST /api/resident_review_status`. */
export const DOCUMENT_REVIEW_CATALOG_KEY: Partial<Record<ReimbursementDocumentType, string>> = {
  signed_lease: 'lease_agreement',
  original_lease: 'original_lease_agreement',
  current_ledger: 'payment_ledger',
  original_ledger: 'original_payment_ledger',
  final_ledger: 'final_payment_ledger',
  skip_affidavit: 'skip_claim_affidavit',
  eviction_lawsuit: 'eviction_lawsuit',
  cares_act_notice: 'notice_to_vacate',
  alternate_court_document: 'alternate_court_documents',
  bankruptcy_document: 'bankruptcy_petition',
  court_registry: 'appeal_document',
  stipulation_agreement: 'stipulation_agreement',
  default_stipulation: 'default_stipulation',
  bond_registry_receipt: 'court_registry_rent_bond',
}

export function getDocumentReviewCatalogKey(
  docType: ReimbursementDocumentType,
): string | undefined {
  return DOCUMENT_REVIEW_CATALOG_KEY[docType]
}

export function buildDocumentReviewPayload(
  docType: ReimbursementDocumentType,
  reviewStatus: 'APPROVED' | 'INCOMPLETE',
  options?: {
    reason?: string
    email_send?: boolean
    reimbursement_attachment_id?: string | number
  },
): ReviewResidentPayload | null {
  const catalogKey = getDocumentReviewCatalogKey(docType)
  if (!catalogKey) return null

  const payload: ReviewResidentPayload = {
    status: reviewStatus,
    email_send: options?.email_send ?? false,
    [catalogKey]: true,
  }
  // Pre–3.0 spelling-fix APIs expect `evication_lawsuit` as the catalog key.
  if (docType === 'eviction_lawsuit') {
    payload.evication_lawsuit = true
  }

  if (options?.reason?.trim()) {
    payload.reason = options.reason.trim()
  }
  if (options?.reimbursement_attachment_id != null && options.reimbursement_attachment_id !== '') {
    payload.reimbursement_attachment_id = options.reimbursement_attachment_id
  }

  return payload
}

/** POST /api/resident_review_status — move attachment to another catalog slot (status RECLASSIFY). */
export function buildDocumentReclassifyPayload(
  fromDocType: ReimbursementDocumentType,
  toDocType: ReimbursementDocumentType,
  options?: {
    reimbursement_attachment_id?: string | number
  },
): ReviewResidentPayload | null {
  const fromKey = getDocumentReviewCatalogKey(fromDocType)
  const toKey = getDocumentReviewCatalogKey(toDocType)
  if (!fromKey || !toKey || fromKey === toKey) return null

  const payload: ReviewResidentPayload = {
    status: 'RECLASSIFY',
    new_document_type: toKey,
    email_send: false,
    [fromKey]: true,
  }
  if (fromDocType === 'eviction_lawsuit') {
    payload.evication_lawsuit = true
  }

  if (options?.reimbursement_attachment_id != null && options.reimbursement_attachment_id !== '') {
    payload.reimbursement_attachment_id = options.reimbursement_attachment_id
  }

  return payload
}

/** Display label for reimbursement_attachment.reclassify (catalog key or backend type constant). */
export function resolveReimbursementDocumentTypeLabel(typeOrKey: string | null | undefined): string {
  const raw = String(typeOrKey ?? '').trim()
  if (!raw) return ''

  for (const [docType, catalogKey] of Object.entries(DOCUMENT_REVIEW_CATALOG_KEY)) {
    if (catalogKey === raw || catalogKey === raw.toLowerCase()) {
      return DOCUMENT_TYPE_LABELS[docType as ReimbursementDocumentType] ?? raw
    }
  }

  const normalized = raw.toUpperCase()
  const backendTypeToDocType: Partial<Record<string, ReimbursementDocumentType>> = {
    LEASE_AGREEMENT: 'signed_lease',
    ORIGINAL_LEASE_AGREEMENT: 'original_lease',
    PAYMENT_LEDGER: 'current_ledger',
    ORIGINAL_PAYMENT_LEDGER: 'original_ledger',
    FINAL_PAYMENT_LEDGER: 'final_ledger',
    SKIP_CLAIM_AFFIDAVIT: 'skip_affidavit',
    NOTICE_TO_VACATE: 'cares_act_notice',
    EVICTION_LAWSUIT: 'eviction_lawsuit',
    ALTERNATE_COURT_DOCUMENT: 'alternate_court_document',
    BANKRUPTCY_PETITION: 'bankruptcy_document',
    APPEAL_DOCUMENT: 'court_registry',
    COURT_REGISTRY_RENT_BOND: 'bond_registry_receipt',
    STIPULATION_AGREEMENT: 'stipulation_agreement',
    DEFAULT_STIPULATION: 'default_stipulation',
  }

  const mapped = backendTypeToDocType[normalized]
  if (mapped) {
    return DOCUMENT_TYPE_LABELS[mapped] ?? raw
  }

  return raw
}
